import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import NotFound from "@/pages/NotFound";
import AccessDenied from "@/pages/AccessDenied";
import { AdminAppShell } from "../../design-system/shells/AdminAppShell";
import { useAuth } from "../../lib/auth";
import { api } from "../../lib/api";
import { errMsg } from "../../lib/portfolio";
import { FINANCE_ROLES, usePhaseFeature } from "../../lib/phaseFeatures";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card, CardContent } from "../../components/ui/card";
import { ApPanel } from "./ApPanel";
import { WaiverPanel } from "./WaiverPanel";

export const money = (c) => `$${(c / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
export const toCents = (s) => Math.round(Number(String(s).replace(/[$,]/g, "")) * 100);

export function FinanceGate({ children }) {
  const { user } = useAuth();
  const f = usePhaseFeature(5, user?.id);
  if (!f) return <div className="p-8 text-sm font-nav text-erf-dark/70" data-testid="phase5-gate-loading">Loading…</div>;
  if (!FINANCE_ROLES.includes(user?.role_key)) return <AccessDenied />; // B2a: role check first → 403 page (no module-state leak)
  if (!f.enabled) return <NotFound />;
  return children;
}

const Lbl = ({ label, children }) => <label className="text-sm font-nav text-erf-dark">{label}{children}</label>;

function NewContract({ pid, onDone }) {
  const [sum, setSum] = useState("");
  const [error, setError] = useState(null);
  const go = (e) => {
    e.preventDefault();
    api.post(`/phase5/projects/${pid}/contract`, { contract_sum_cents: toCents(sum) }).then(onDone)
      .catch((err) => setError(errMsg(err, "Could not create the contract.")));
  };
  return (
    <form onSubmit={go} className="flex flex-wrap items-end gap-3" data-testid="phase5-contract-form">
      <Lbl label="Contract sum ($)"><Input required inputMode="decimal" value={sum} onChange={(e) => setSum(e.target.value)} className="mt-1 h-11" data-testid="phase5-contract-sum" /></Lbl>
      <Button type="submit" className="h-11 bg-erf-gold text-erf-dark hover:bg-erf-goldHover" data-testid="phase5-contract-create">Create contract</Button>
      <p className="w-full text-xs font-nav text-erf-dark/80">Retention starts at 0%. Only the Founder can change it.</p>
      {error && <p role="alert" className="w-full text-sm text-[#991B1B]" data-testid="phase5-contract-error">{error}</p>}
    </form>
  );
}

function ContractPanel({ c, writer, founder, reload }) {
  const [desc, setDesc] = useState("");
  const [val, setVal] = useState("");
  const [ret, setRet] = useState(String(c.retention_bps / 100));
  const [period, setPeriod] = useState(new Date().toISOString().slice(0, 10));
  const [error, setError] = useState(null);
  const run = (p) => p.then(() => { setError(null); reload(); }).catch((e) => setError(errMsg(e, "That did not work.")));
  const balanced = c.sov_total_cents === c.contract_sum_cents;
  return (
    <Card className="ds-card border-erf-borderCream" data-testid="phase5-contract-panel">
      <CardContent className="p-5 space-y-4 text-sm font-nav text-erf-dark">
        <h2 className="font-display font-semibold text-base md:text-lg">Contract {money(c.contract_sum_cents)} · retention {(c.retention_bps / 100).toFixed(2)}%</h2>
        {founder && (
          <div className="flex flex-wrap items-end gap-3">
            <Lbl label="Retention %"><Input inputMode="decimal" value={ret} onChange={(e) => setRet(e.target.value)} className="mt-1 h-11 w-28" data-testid="phase5-retention-input" /></Lbl>
            <Button variant="outline" className="h-11 border-erf-borderCream" onClick={() => run(api.patch(`/phase5/contracts/${c.id}/retention`, { retention_bps: Math.round(Number(ret) * 100) }))} data-testid="phase5-retention-save">Save retention</Button>
          </div>
        )}
        <h2 className="font-display font-semibold text-base md:text-lg">Schedule of values</h2>
        <ul className="space-y-1" data-testid="phase5-sov-list">
          {c.sov_lines.map((l) => <li key={l.id}>{l.item_no}. {l.description} — {money(l.scheduled_value_cents)}</li>)}
          {c.sov_lines.length === 0 && <li>No lines yet.</li>}
        </ul>
        <p data-testid="phase5-sov-total">Total {money(c.sov_total_cents)} of {money(c.contract_sum_cents)}{balanced ? " — balanced" : " — must equal the contract sum before billing"}</p>
        {writer && c.pay_apps.length === 0 && (
          <form className="flex flex-wrap items-end gap-3" onSubmit={(e) => { e.preventDefault(); run(api.post(`/phase5/contracts/${c.id}/sov-lines`, { description: desc, scheduled_value_cents: toCents(val) }).then(() => { setDesc(""); setVal(""); })); }}>
            <Lbl label="Line description"><Input required value={desc} onChange={(e) => setDesc(e.target.value)} className="mt-1 h-11" data-testid="phase5-sov-desc" /></Lbl>
            <Lbl label="Scheduled value ($)"><Input required inputMode="decimal" value={val} onChange={(e) => setVal(e.target.value)} className="mt-1 h-11" data-testid="phase5-sov-value" /></Lbl>
            <Button type="submit" className="h-11 bg-erf-dark text-[#FEFCF7]" data-testid="phase5-sov-add">Add line</Button>
          </form>
        )}
        <h2 className="font-display font-semibold text-base md:text-lg">Pay applications</h2>
        <ul className="space-y-2">
          {c.pay_apps.map((a) => (
            <li key={a.id}><Link to={`/phase5/pay-apps/${a.id}`} className="inline-flex min-h-[44px] items-center underline text-erf-dark" data-testid={`phase5-app-link-${a.number}`}>
              No. {a.number} · period to {a.period_to} · {a.state}</Link></li>
          ))}
        </ul>
        {writer && balanced && (
          <div className="flex flex-wrap items-end gap-3">
            <Lbl label="Period to"><Input type="date" value={period} onChange={(e) => setPeriod(e.target.value)} className="mt-1 h-11" data-testid="phase5-app-period" /></Lbl>
            <Button className="h-11 bg-erf-gold text-erf-dark hover:bg-erf-goldHover" onClick={() => run(api.post(`/phase5/contracts/${c.id}/pay-apps`, { period_to: period }))} data-testid="phase5-app-create">New pay application</Button>
          </div>
        )}
        {error && <p role="alert" className="text-sm text-[#991B1B]" data-testid="phase5-contract-panel-error">{error}</p>}
      </CardContent>
    </Card>
  );
}

export default function BillingWorkspace() {
  const { user } = useAuth();
  const [projects, setProjects] = useState(null);
  const [pid, setPid] = useState(null);
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/phase5/projects").then((r) => { setProjects(r.data); setPid(r.data.items[0]?.id || null); }); }, []);
  const load = useCallback(() => { if (pid) api.get(`/phase5/projects/${pid}/contract`).then((r) => setData(r.data)); }, [pid]);
  useEffect(() => { load(); }, [load]);
  const role = projects?.role_key;
  const writer = ["OWNER_SUPER_ADMIN", "BOOKKEEPER_ACCOUNTANT"].includes(role);
  return (
    <AdminAppShell activeNav="finance" user={user} title="Financials" subtitle="Progress billing: schedule of values and pay applications."
      breadcrumbs={[{ label: "Owner Command Center", to: "/owner" }, { label: "Financials", to: null }]}>
      <div data-testid="phase5-workspace">
        <p className="mb-4 text-sm font-nav text-erf-dark/80" data-testid="phase5-boundary-note">Records only. Nothing here sends an invoice or moves money. Card, bank and accounting connections are waiting on external authorization.</p>
        {projects && projects.items.length === 0 && <p className="text-sm font-nav text-erf-dark" data-testid="phase5-no-projects">No projects yet.</p>}
        <div className="flex flex-wrap gap-2 mb-5" role="group" aria-label="Choose a project" data-testid="phase5-project-picker">
          {(projects?.items || []).map((p) => (
            <Button key={p.id} aria-pressed={pid === p.id} onClick={() => setPid(p.id)} data-testid={`phase5-project-${p.id}`}
              variant={pid === p.id ? "default" : "outline"} className={pid === p.id ? "h-11 bg-erf-dark text-[#FEFCF7]" : "h-11 border-erf-borderCream text-erf-dark bg-white"}>{p.name}</Button>
          ))}
        </div>
        {data && !data.contract && (writer ? <NewContract pid={pid} onDone={load} /> : <p className="text-sm font-nav text-erf-dark" data-testid="phase5-no-contract">No contract yet.</p>)}
        {data?.contract && <ContractPanel key={data.contract.id} c={data.contract} writer={writer} founder={role === "OWNER_SUPER_ADMIN"} reload={load} />}
        {pid && role && <ApPanel key={pid} pid={pid} role={role} />}
        {pid && role && <WaiverPanel key={`w-${pid}`} pid={pid} writer={writer} />}
      </div>
    </AdminAppShell>
  );
}
